import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import {
  Search,
  Plus,
  ChevronDown,
  RotateCcw,
  Calendar,
  MoreVertical,
  Eye,
  Printer,
  Trash2,
  CheckCircle2,
  Filter,
  Loader2,
  SlidersHorizontal as Filters,
  X,
  Clock,
  ClipboardList,
  ChevronUp,
  LayoutDashboard
} from 'lucide-react';
import Pagination from '../../components/Common/Pagination/Pagination';
import styles from './InspectionTracking.module.css';
import Footer from '../../components/Common/Footer/Footer';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import NoData from '../../components/Common/NoData/NoData';

const InspectionTracking = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Data State
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [summary, setSummary] = useState({ pending: 0, completed: 0, total: 0 });
  const [filterData, setFilterData] = useState({ projects: [] });

  // Filter/Pagination State
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ project_id: '', status: '' });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const toggleActionMenu = (index, e) => {
    e.stopPropagation();
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 180 });
      setOpenMenuIndex(index);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles['action-wrapper']}`) && !event.target.closest(`.${styles['action-dropdown']}`)) {
        setOpenMenuIndex(null);
      }
    };
    if (openMenuIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuIndex]);

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchFilterData = async () => {
    try {
      const response = await api.get('/projects-dropdown');
      if (response.data.success) {
        setFilterData({ projects: response.data.data });
      }
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  useEffect(() => {
    fetchInspections();
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  const fetchInspections = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        search: searchTerm,
        project_id: filters.project_id,
        status: filters.status
      };
      const response = await api.get('/inspections', { params });
      if (response.data.success) {
        setInspections(response.data.data.data);
        setTotalItems(response.data.data.total);
        if (response.data.summary) {
          setSummary({
            pending: response.data.summary.pending,
            completed: response.data.summary.approved + response.data.summary.rejected,
            total: response.data.summary.total
          });
        }
      }
    } catch (error) {
      toast.error('Failed to fetch inspections');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getClearanceClass = (status) => {
    if (!status) return 'pill-pending';
    switch (status.toLowerCase()) {
      case 'approved': return 'pill-approved';
      case 'pending': return 'pill-pending';
      case 'rejected': return 'pill-rejected';
      default: return 'pill-pending';
    }
  };

  const getDispatchStatus = (clearance) => {
    return clearance?.toLowerCase() === 'approved' ? 'Allowed' : 'Blocked';
  };

  const getDispatchClass = (status) => {
    return status.toLowerCase() === 'allowed' ? 'pill-allowed' : 'pill-blocked';
  };

  const metrics = [
    {
      label: 'Pending Review',
      value: summary.pending,
      icon: <Clock size={20} />,
      theme: 'orange'
    },
    {
      label: 'Total Inspections',
      value: summary.total,
      icon: <ClipboardList size={20} />,
      theme: 'blue'
    },
    {
      label: 'Completed',
      value: summary.completed,
      icon: <CheckCircle2 size={20} />,
      theme: 'green',
      highlight: true
    }
  ];

  return (
    <div className={`${styles['inspection-tracking-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Inspection Tracking</h1>
              <p>Real-time monitoring of material quality assurance and clearance workflows.</p>
            </div>
            <button className={styles['add-btn']} onClick={() => navigate('/inspections/schedule')}>
              <Plus size={20} />
              <span>Schedule Inspection</span>
            </button>
          </div>

          {/* Metrics — collapsible on mobile */}
          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(prev => !prev)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {metrics[0]?.value} Pending · {metrics[2]?.value} Completed
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {metrics.map((m, i) => (
                <div key={i} className={`${styles['metric-card']} ${styles[m.theme]} ${m.highlight ? styles['highlight-card'] : ''}`}>
                  <div className={styles['metric-icon-box']}>
                    {m.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>{m.label}</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{m.value}</span>
                    </div>
                  </div>
                  {m.highlight && <div className={styles['glow-effect']}></div>}
                </div>
              ))}
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search by ID, Project, Vendor or PO..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                  onChange={(option) => { setItemsPerPage(option.value); setCurrentPage(1); }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>

              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {(filters.project_id || filters.status) && (
                  <span className={styles['filter-badge']}></span>
                )}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Inspections</h3>
                  <p>Filter records by project or status.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Project</label>
                  <CustomSelect
                    value={filters.project_id ? {
                      value: filters.project_id,
                      label: filterData.projects.find(p => p.id === parseInt(filters.project_id))?.project_name
                    } : null}
                    onChange={(opt) => {
                      setFilters(prev => ({ ...prev, project_id: opt ? opt.value : '' }));
                      setCurrentPage(1);
                    }}
                    options={filterData.projects.map(p => ({ value: p.id, label: p.project_name }))}
                    placeholder="All Projects"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    value={filters.status ? {
                      value: filters.status,
                      label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1).toLowerCase()
                    } : null}
                    onChange={(opt) => {
                      setFilters(prev => ({ ...prev, status: opt ? opt.value : '' }));
                      setCurrentPage(1);
                    }}
                    options={[
                      { value: 'pending', label: 'Pending' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'rejected', label: 'Rejected' }
                    ]}
                    placeholder="All Statuses"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button
                  className={styles['reset-btn']}
                  onClick={() => {
                    setFilters({ project_id: '', status: '' });
                    setCurrentPage(1);
                  }}
                >
                  Reset
                </button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            {/* Desktop View */}
            <div className={styles['table-wrapper']}>
              <table className={styles['inspection-table']}>
                <thead>
                  <tr>
                    <th>INSPECTION ID</th>
                    <th>PROJECT</th>
                    <th>PO NUMBER</th>
                    <th>VENDOR</th>
                    <th>AGENCY</th>
                    <th>NOTIFICATION</th>
                    <th>INSPECTION DATE</th>
                    <th>CLEARANCE</th>

                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="10" style={{ textAlign: 'center', padding: '100px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                          <Loader2 size={40} className={styles['spinner']} style={{ color: '#f97316' }} />
                          <span style={{ color: '#64748b', fontWeight: 600 }}>Fetching technical details...</span>
                        </div>
                      </td>
                    </tr>
                  ) : inspections.length > 0 ? (
                    inspections.map((item, index) => {
                      const dispatchStatus = getDispatchStatus(item.clearance_status);
                      return (
                        <tr key={item.id}>
                          <td className={styles['id-cell']} onClick={() => navigate(`/inspections/${item.id}`)} style={{ cursor: 'pointer' }}>
                            <div className={styles['receipt-info']}>
                              <span className={styles['receipt-text']}>#{item.id}</span>
                            </div>
                          </td>
                          <td className={styles['project-cell']}>{item.project?.project_name || '---'}</td>
                          <td className={styles['po-cell']}>{item.purchase_order?.po_number || '---'}</td>
                          <td>{item.vendor?.vendor_name || '---'}</td>
                          <td style={{ fontWeight: 700, color: '#f59e0b' }}>{item.agency_type}</td>
                          <td className={styles['date-cell']}>{new Date(item.notified_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                          <td className={styles['date-cell']}>{item.actual_inspection_date ? new Date(item.actual_inspection_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '---'}</td>
                          <td>
                            <span className={`${styles['status-pill']} ${styles['pill-clearance']} ${styles[getClearanceClass(item.clearance_status)]}`}>
                              {(item.clearance_status || 'PENDING').toUpperCase()}
                            </span>
                          </td>
                          <td className={styles['actions-cell']}>
                            <div className={styles['action-wrapper']}>
                              <button
                                className={`${styles['action-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                                onClick={(e) => toggleActionMenu(index, e)}
                              >
                                <MoreVertical size={18} />
                              </button>

                              {/* Global Fixed Action Menu — will be handled by common block below */}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <NoData message="No inspections match your current search/filter." colSpan={10} isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles['inspection-cards']}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 size={32} className={styles['spinner']} style={{ color: '#f59e0b' }} />
                </div>
              ) : inspections.length > 0 ? (
                inspections.map((item, index) => (
                  <div key={item.id} className={styles['inspection-card']}>
                    <div className={styles['card-main']}>
                      <div className={styles['card-info']}>
                        <span className={styles['card-id']} onClick={() => navigate(`/inspections/${item.id}`)}>
                          #{item.id}
                        </span>
                        <span className={styles['card-po']}>{item.purchase_order?.po_number || '---'}</span>
                      </div>
                      <div className={styles['action-wrapper']}>
                        <button
                          className={`${styles['action-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                          onClick={(e) => toggleActionMenu(index, e)}
                        >
                          <MoreVertical size={18} />
                        </button>
                      </div>
                    </div>

                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                        <span className={styles['detail-label']}>Project</span>
                        <span className={styles['detail-value']}>{item.project?.project_name || '---'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Agency</span>
                        <span className={styles['detail-value']} style={{ color: '#f59e0b' }}>{item.agency_type}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Vendor</span>
                        <span className={styles['detail-value']}>{item.vendor?.vendor_name || '---'}</span>
                      </div>
                    </div>

                    <div className={styles['card-footer']}>
                      <div className={styles['footer-left']}>
                        <Calendar size={14} className={styles['text-muted']} />
                        <span>{item.actual_inspection_date ? new Date(item.actual_inspection_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '---'}</span>
                      </div>
                      <div className={styles['footer-statuses']}>
                        <span className={`${styles['status-pill']} ${styles['pill-clearance']} ${styles[getClearanceClass(item.clearance_status)]}`}>
                          {(item.clearance_status || 'PENDING').toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <NoData message="No records found." isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>

          {/* Global Fixed Action Menu */}
          {openMenuIndex !== null && (
            <div
              className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                zIndex: 9999
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => navigate(`/inspections/${inspections[openMenuIndex]?.id}`)}>
                <Eye size={16} /> View Details
              </button>
              <button onClick={() => navigate(`/inspections/edit/${inspections[openMenuIndex]?.id}`)}>
                <Calendar size={16} /> Edit Schedule
              </button>
              <div className={styles['dropdown-divider']}></div>
              <button onClick={() => navigate(`/inspections/clearance/${inspections[openMenuIndex]?.id}`)}>
                <CheckCircle2 size={16} /> Clearance
              </button>
            </div>
          )}
          <Footer />
        </main>
      </div>
    </div>
  );
};

export default InspectionTracking;
